import React from "react";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import moment from "moment";

import { enGB } from "date-fns/locale/en-GB";
registerLocale("en-GB", enGB);

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/liveOps/bonuses/slice";

import cn from "classnames";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { getDatePickerFormats, useTimeFormat } from "helpers/timeFormat";
import {
  BONUS_CADENCES,
  BONUS_MONTH_DAYS,
  BONUS_WEEKDAYS,
  findBonusTypeTitle,
  RELOAD_BONUS_TYPE,
  RECURRING_BONUS_TYPE,
} from "helpers/bonusScheduleTypeData";

import Tooltip from "../../../../components/Tooltip";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
}

const BonusesTimingTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
}) => {
  const { t } = useTranslation();
  const datePickerFormats = getDatePickerFormats(useTimeFormat());
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store.bonuses.payload);
  const typesList = useAppSelector((store) => store.bonuses.typesList);
  const typeTitle = findBonusTypeTitle(typesList, payload?.typeId);
  const isRecurring = typeTitle === RECURRING_BONUS_TYPE;
  const isReload = typeTitle === RELOAD_BONUS_TYPE;

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const weekdayOptions = BONUS_WEEKDAYS.map((weekday) => ({
    value: weekday,
    label: t(weekday),
  }));
  const cadenceOptions = BONUS_CADENCES.map((cadence) => ({
    value: cadence,
    label: t(cadence),
  }));
  const monthDayOptions = BONUS_MONTH_DAYS.map((day) => ({
    value: day,
    label: day,
  }));

  const scheduleTime = (value?: string) =>
    value && moment(value, "HH:mm", true).isValid()
      ? moment(value, "HH:mm", true).toDate()
      : null;

  const commitScheduleTime = (field: string, value: string) => {
    handleData(field, value);
    clearFieldError(field);
  };

  const renderScheduleTime = (field: string, label: string, value?: string) => (
    <div
      className={cn("date-picker-wrapper", {
        "has-error": fieldErrors?.[field],
      })}
    >
      <div className="title">{label}</div>
      <DatePicker
        locale="en-GB"
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={30}
        timeCaption={label}
        dateFormat="HH:mm"
        timeFormat="HH:mm"
        placeholderText="HH:mm"
        strictParsing
        value={value ?? ""}
        selected={scheduleTime(value)}
        onChangeRaw={(event) => {
          const changeEvent = event as
            | React.ChangeEvent<HTMLInputElement>
            | undefined;
          changeEvent?.preventDefault();
          commitScheduleTime(field, changeEvent?.target?.value ?? "");
        }}
        onChange={(date) => {
          commitScheduleTime(field, date ? moment(date).format("HH:mm") : "");
        }}
      />
    </div>
  );

  return (
    <div className="bonus-form-content">
      <div className="bonus-timing-wrap">
        <div className="bonus-form-box">
          {isRecurring && (
            <div className="bonus-form-box--item">
              <DefaultDropdown
                value={payload?.recurringCadence}
                placeholder={t("cadence")}
                label={t("cadence")}
                options={cadenceOptions}
                onSelect={(value) => {
                  const cadence = String(value);
                  handleData("recurringCadence", cadence);
                  if (cadence !== "weekly") {
                    handleData("recurringWeekday", "");
                  }
                  if (cadence !== "monthly") {
                    handleData("recurringDayOfMonth", "");
                  }
                  clearFieldError("recurringCadence");
                  clearFieldError("recurringWeekday");
                  clearFieldError("recurringDayOfMonth");
                }}
                hasError={Boolean(fieldErrors?.["recurringCadence"])}
              />
            </div>
          )}
          {isRecurring && payload?.recurringCadence === "weekly" && (
            <div className="bonus-form-box--item">
              <DefaultDropdown
                value={payload?.recurringWeekday}
                placeholder={t("weekday")}
                label={t("weekday")}
                options={weekdayOptions}
                onSelect={(value) => {
                  handleData("recurringWeekday", String(value));
                  clearFieldError("recurringWeekday");
                }}
                hasError={Boolean(fieldErrors?.["recurringWeekday"])}
              />
            </div>
          )}
          {isRecurring && payload?.recurringCadence === "monthly" && (
            <div className="bonus-form-box--item">
              <DefaultDropdown
                value={payload?.recurringDayOfMonth}
                placeholder={t("dayOfMonth")}
                label={t("dayOfMonth")}
                options={monthDayOptions}
                onSelect={(value) => {
                  handleData("recurringDayOfMonth", String(value));
                  clearFieldError("recurringDayOfMonth");
                }}
                hasError={Boolean(fieldErrors?.["recurringDayOfMonth"])}
              />
            </div>
          )}
          {isReload && (
            <>
              <div className="bonus-form-box--item">
                <DefaultDropdown
                  value={payload?.reloadStartWeekday}
                  placeholder={t("from")}
                  label={t("from")}
                  options={weekdayOptions}
                  onSelect={(value) => {
                    handleData("reloadStartWeekday", String(value));
                    clearFieldError("reloadStartWeekday");
                  }}
                  hasError={Boolean(fieldErrors?.["reloadStartWeekday"])}
                />
              </div>
              <div className="bonus-form-box--item">
                {renderScheduleTime(
                  "reloadStartTime",
                  t("time"),
                  payload?.reloadStartTime,
                )}
              </div>
            </>
          )}
          <div className="bonus-form-box--item">
            <div
              className={cn("date-picker-wrapper", {
                "has-error": fieldErrors?.["startedAt"],
              })}
            >
              <div className="title">
                {t("startDate")}
                <Tooltip
                  text={t("dateWhenBonusCampaignActive")}
                  position="top-left"
                >
                  <span className="info-icon">ℹ</span>
                </Tooltip>
              </div>
              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                showTimeSelect
                timeFormat={datePickerFormats.timeFormat}
                placeholderText={datePickerFormats.placeholder}
                dateFormat={datePickerFormats.dateFormat}
                selected={
                  payload?.startedAt
                    ? moment(
                        payload?.startedAt,
                        "DD.MM.YYYY HH:mm",
                        true,
                      ).toDate()
                    : null
                }
                onChange={(date) => {
                  handleData(
                    "startedAt",
                    moment(date).format("DD.MM.YYYY HH:mm"),
                  );
                  clearFieldError("startedAt");
                }}
              />
            </div>
          </div>
          <div className="bonus-form-box--item">
            <div
              className={cn("date-picker-wrapper", {
                "has-error": fieldErrors?.["finishedAt"],
              })}
            >
              <div className="title">
                {t("endDate")}{" "}
                <Tooltip text={t("dateAndTimeBonusCloses")} position="top-left">
                  <span className="info-icon">ℹ</span>
                </Tooltip>
              </div>
              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                showTimeSelect
                timeFormat={datePickerFormats.timeFormat}
                placeholderText={datePickerFormats.placeholder}
                dateFormat={datePickerFormats.dateFormat}
                selected={
                  payload?.finishedAt
                    ? moment(
                        payload?.finishedAt,
                        "DD.MM.YYYY HH:mm",
                        true,
                      ).toDate()
                    : null
                }
                onChange={(date) => {
                  handleData(
                    "finishedAt",
                    moment(date).format("DD.MM.YYYY HH:mm"),
                  );
                  clearFieldError("finishedAt");
                }}
              />
            </div>
          </div>
          <div className="bonus-form-box--item">
            <div
              className={cn("date-picker-wrapper", {
                "has-error": fieldErrors?.["showPreviousAt"],
              })}
            >
              <div className="title">
                {t("showOffer")}{" "}
                <Tooltip text={t("showOfferPreviousAt")} position="top-left">
                  <span className="info-icon">ℹ</span>
                </Tooltip>
              </div>
              <DatePicker
                locale="en-GB"
                showTimeSelect
                timeFormat={datePickerFormats.timeFormat}
                placeholderText={datePickerFormats.placeholder}
                dateFormat={datePickerFormats.dateFormat}
                selected={
                  payload?.showPreviousAt
                    ? moment(
                        payload?.showPreviousAt,
                        "DD.MM.YYYY HH:mm",
                        true,
                      ).toDate()
                    : null
                }
                onChange={(date) =>
                  handleData(
                    "showPreviousAt",
                    moment(date).format("DD.MM.YYYY HH:mm"),
                  )
                }
              />
            </div>
          </div>
        </div>
        <div className="bonus-form-box">
          {isRecurring && (
            <div className="bonus-form-box--item">
              {renderScheduleTime(
                "recurringGrantTime",
                t("time"),
                payload?.recurringGrantTime,
              )}
            </div>
          )}
          {isReload && (
            <>
              <div className="bonus-form-box--item">
                <DefaultDropdown
                  value={payload?.reloadEndWeekday}
                  placeholder={t("toLabel")}
                  label={t("toLabel")}
                  options={weekdayOptions}
                  onSelect={(value) => {
                    handleData("reloadEndWeekday", String(value));
                    clearFieldError("reloadEndWeekday");
                  }}
                  hasError={Boolean(fieldErrors?.["reloadEndWeekday"])}
                />
              </div>
              <div className="bonus-form-box--item">
                {renderScheduleTime(
                  "reloadEndTime",
                  t("time"),
                  payload?.reloadEndTime,
                )}
              </div>
            </>
          )}
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.claimPeriodInHours?.toString() ?? ""}
              placeholder={t("enterValue")}
              label={t("claimPeriodInHours")}
              tooltipText={t("claimPeriodTooltip")}
              tooltipPosition="top"
              type="number"
              id="bonus-claimPeriodInHours"
              onChange={(e) => {
                handleData("claimPeriodInHours", e);
                clearFieldError("claimPeriodInHours");
              }}
              hasError={Boolean(fieldErrors?.["claimPeriodInHours"])}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.activationPeriodInHours?.toString() ?? ""}
              placeholder={t("enterValue")}
              label={t("activationPeriodInHours")}
              tooltipText={t("theNumberOfHoursGiven")}
              tooltipPosition="top"
              type="number"
              id="bonus-activationPeriodInHours"
              onChange={(e) => {
                handleData("activationPeriodInHours", e);
                clearFieldError("activationPeriodInHours");
              }}
              hasError={Boolean(fieldErrors?.["activationPeriodInHours"])}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.wageringPeriodInHours?.toString() ?? ""}
              placeholder={t("enterValue")}
              label={t("wageringPeriodInHours")}
              tooltipText={t("theNumberOfHours")}
              tooltipPosition="top"
              type="number"
              id="bonus-wageringPeriodInHours"
              onChange={(e) => {
                handleData("wageringPeriodInHours", e);
                clearFieldError("wageringPeriodInHours");
              }}
              hasError={Boolean(fieldErrors?.["wageringPeriodInHours"])}
            />
          </div>
        </div>
        {(isRecurring || isReload) && (
          <p className="bonus-schedule-note">{t("scheduleTimesUtc")}</p>
        )}
      </div>
    </div>
  );
};

export default BonusesTimingTab;
